import { useEffect, useMemo } from 'react'
import { Controller, useFormContext } from 'react-hook-form'

import { Box, Text } from '@factorialco/deprecated-design-system'
import { F0Select } from '@factorialco/f0-react'
import { EntitySelect, EntitySelectEntity, Input } from '@factorialco/f0-react/dist/experimental'
import { AvatarNameSelectorDocument } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'
import buildUrl from 'modules/core/lib/buildUrl'
import i18n from 'modules/core/lib/i18n'

import { RequestDocumentHandlerSchema } from 'modules/procurement/components/procurementTypes/Workflows/sidePanels/schemas/requestDocumentHandler'
import { HandlerFormComponent } from 'modules/workflows/contexts/WorkflowContext'
import { getDurationUnitOptions } from 'modules/workflows/lib/durationUnit'

export const RequestDocumentHandlerForm = ({}: HandlerFormComponent) => {
  const { control, watch, setValue, trigger } = useFormContext<RequestDocumentHandlerSchema>()

  const { company } = useCurrentSession()

  const { loading: employeesLoading, data: employeesData } = useQuery(AvatarNameSelectorDocument, {
    variables: {
      companyId: company.id,
      active: true,
    },
  })

  const PURCHASE_REQUESTER_ID = 'purchase_requester'
  const PURCHASE_REQUESTER_ENTITY: EntitySelectEntity = useMemo(
    () => ({
      id: PURCHASE_REQUESTER_ID,
      name: i18n.t('workflows.sidepanel.request_document.assignees_purchase_requester_label'),
      avatar: undefined,
      expanded: false,
    }),
    []
  )

  // Watch root-level title and description for syncing
  const rootTitle = watch('title')
  const rootDescription = watch('description')

  // Sync from BasicInputs (root level) to payload
  useEffect(() => {
    setValue('nodeData.payload.title', rootTitle ?? '')
    trigger('nodeData.payload.title')
    setValue('nodeData.payload.description', rootDescription ?? '')
    trigger('nodeData.payload.description')
  }, [rootTitle, rootDescription])

  const selectedAssigneeIds = watch('nodeData.payload.assignee_ids') ?? []

  const availableEmployees = employeesData?.employees?.employeeNames || []

  const selectedAssigneeEntities = [
    ...(selectedAssigneeIds.includes(PURCHASE_REQUESTER_ID)
      ? [
          {
            id: PURCHASE_REQUESTER_ID,
            name: i18n.t('workflows.sidepanel.request_document.assignees_purchase_requester_label'),
            avatar: undefined, // TODO: Add a fallback avatar for generic user
          },
        ]
      : []),
    ...availableEmployees
      .filter((emp) => selectedAssigneeIds.includes(String(emp.id)))
      .map((emp) => ({
        id: String(emp.id),
        name: emp.fullName,
        avatar: buildUrl(emp.avatar?.url),
      })),
  ]

  useEffect(() => {
    const isPurchaseRequesterSelected = selectedAssigneeIds.includes(PURCHASE_REQUESTER_ID)
    setValue('nodeData.payload.assign_to_requester', isPurchaseRequesterSelected)
  }, [selectedAssigneeIds, setValue])

  return (
    <Box gap="s16">
      {/* Task Title */}
      <Box gap="s8" width="full">
        <Controller
          name="nodeData.payload.title"
          control={control}
          render={({ field, fieldState }) => (
            <Input
              required
              size="md"
              label={i18n.t('workflows.sidepanel.request_document.title_label')}
              placeholder={i18n.t('workflows.sidepanel.request_document.title_placeholder')}
              value={field.value}
              onChange={(value) => {
                field.onChange(value)
                setValue('title', value)
                trigger('title')
              }}
              error={fieldState.error?.message}
            />
          )}
        />
      </Box>

      {/* Task Description */}
      <Box gap="s8" width="full">
        <Controller
          name="nodeData.payload.description"
          control={control}
          render={({ field, fieldState }) => (
            <Input
              required
              size="md"
              label={i18n.t('workflows.sidepanel.request_document.description_label')}
              placeholder={i18n.t('workflows.sidepanel.request_document.description_placeholder')}
              value={field.value}
              onChange={(value) => {
                field.onChange(value)
                setValue('description', value)
                trigger('description')
              }}
              error={fieldState.error?.message}
            />
          )}
        />
      </Box>

      {/* Assignees */}
      <Box gap="s8" width="full">
        <Controller
          name="nodeData.payload.assignee_ids"
          control={control}
          render={({ field, fieldState }) => (
            <EntitySelect
              label={i18n.t('workflows.sidepanel.request_document.assignees_label')}
              loading={employeesLoading}
              required
              entities={[
                PURCHASE_REQUESTER_ENTITY,
                ...availableEmployees.map((emp) => ({
                  id: String(emp.id),
                  name: emp.fullName,
                  avatar: buildUrl(emp.avatar?.url),
                  expanded: false,
                })),
              ]}
              notFoundSubtitle={i18n.t(
                'workflows.sidepanel.request_document.assignees_not_found_subtitle'
              )}
              notFoundTitle={i18n.t(
                'workflows.sidepanel.request_document.assignees_not_found_title'
              )}
              onGroupChange={() => {}}
              onItemExpandedChange={() => {}}
              size="md"
              onSelect={(selection: EntitySelectEntity[] | EntitySelectEntity | null) => {
                const newAssigneeIds = Array.isArray(selection)
                  ? selection.map((s) => String(s.id))
                  : selection
                  ? [String(selection.id)]
                  : []
                field.onChange(newAssigneeIds)
                const isPurchaseRequesterSelected = newAssigneeIds.includes(PURCHASE_REQUESTER_ID)
                setValue('nodeData.payload.assign_to_requester', isPurchaseRequesterSelected)
                trigger('nodeData.payload.assignee_ids')
                trigger('nodeData.payload.assign_to_requester')
              }}
              searchPlaceholder={i18n.t(
                'workflows.sidepanel.request_document.assignees_search_placeholder'
              )}
              selectedEntities={selectedAssigneeEntities}
              selectedLabel={i18n.t(
                'workflows.sidepanel.request_document.assignees_selected_label'
              )}
              singleSelector={false}
              placeholder={i18n.t('workflows.sidepanel.request_document.assignees_placeholder')}
              selectedItemsCopy={i18n.t(
                'workflows.sidepanel.request_document.assignees_selected_copy'
              )}
              groups={[]}
              selectedGroup="all"
              clearLabel={i18n.t('workflows.sidepanel.request_document.assignees_clear_label')}
              selectAllLabel={i18n.t(
                'workflows.sidepanel.request_document.assignees_select_all_label'
              )}
              error={fieldState.error?.message}
            />
          )}
        />
      </Box>

      {/* Due On */}
      <Box gap="s8" width="full">
        <Text size="200" weight="medium" className="text-f1-foreground-secondary">
          {i18n.t('workflows.sidepanel.request_document.due_on_label')}
        </Text>
        <Box flexDirection="row" gap="s8" width="full" alignItems="baseline">
          <Box style={{ width: '100px' }}>
            <Controller
              name="nodeData.payload.due_on_value"
              control={control}
              render={({ field, fieldState }) => (
                <Input
                  size="md"
                  label={i18n.t('workflows.sidepanel.request_document.due_on_label')}
                  hideLabel
                  placeholder="2"
                  value={String(field.value)}
                  onChange={(value) => field.onChange(Number(value))}
                  error={fieldState.error?.message}
                  type="number"
                />
              )}
            />
          </Box>
          <Box width="full">
            <Controller
              name="nodeData.payload.due_on_unit"
              control={control}
              render={({ field, fieldState }) => (
                <F0Select
                  size="md"
                  label={i18n.t('workflows.sidepanel.request_document.due_on_label')}
                  hideLabel
                  value={field.value}
                  onChange={field.onChange}
                  error={fieldState.error?.message}
                  options={getDurationUnitOptions()}
                />
              )}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  )
}
